import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { getMe, updateProfile, ApiError } from '../api';
import { listTimezones, describeTimezone, detectTimezone } from '../timezones';

/**
 * Your name and your timezone.
 *
 * Neither could be changed before this — `/api/me` was read-only apart from the day window
 * — so the zone chosen at registration was permanent. That is not a small gap: the zone is
 * what "today" means (INV-UTC), so it decides which day the Today screen shows, when a
 * habit's week starts and ends, and which day a completed task counts towards. Being stuck
 * with the wrong one misreports all of it, quietly.
 *
 * The zone is a text input with a `<datalist>` rather than a `<select>` of 400 options:
 * typing "war" and picking Europe/Warsaw is faster than scrolling, and the list is only a
 * suggestion, so a zone this browser knows but the list omits is still accepted. The server
 * validates it either way.
 */
export function ProfileSetting() {
  const queryClient = useQueryClient();
  const me = useQuery({ queryKey: ['me'], queryFn: getMe });

  const [name, setName] = useState<string | null>(null);
  const [zone, setZone] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);

  // Built once: describing 400 zones means 400 Intl formatters, which is not something to
  // redo on every keystroke.
  const zones = useMemo(() => {
    const now = new Date();
    return listTimezones().map((z) => ({ id: z, label: describeTimezone(z, now) }));
  }, []);

  const save = useMutation({
    mutationFn: () =>
      updateProfile({
        ...(name === null ? {} : { displayName: name.trim() }),
        ...(zone === null ? {} : { timezone: zone.trim() }),
      }),
    onSuccess: () => {
      setError(null);
      setSaved(true);
      setName(null);
      setZone(null);
      void queryClient.invalidateQueries();
    },
    onError: (e) => {
      setSaved(false);
      setError(e instanceof ApiError ? e.message : 'Could not save that.');
    },
  });

  if (!me.data) return null;

  // Null means "not edited", so the field shows the saved value until it is touched. Keeping
  // a copy in state from the start would discard a refetch, or fight with one.
  const nameValue = name ?? me.data.displayName;
  const zoneValue = zone ?? me.data.timezone;
  const detected = detectTimezone();
  const changed = nameValue.trim() !== me.data.displayName || zoneValue.trim() !== me.data.timezone;

  return (
    <section className="card">
      <h3>You</h3>
      <p className="muted">
        Your timezone decides what “today” means — which day the planner shows, when a habit’s week
        turns over, and which day finished work counts towards.
      </p>

      <div className="row gap profile-fields">
        <label className="subtext">
          Name
          <input
            value={nameValue}
            aria-label="Display name"
            onChange={(e) => {
              setName(e.target.value);
              setSaved(false);
            }}
          />
        </label>

        <label className="subtext">
          Timezone
          <input
            list="timezone-options"
            className="timezone-input"
            value={zoneValue}
            aria-label="Timezone"
            placeholder="Europe/Warsaw"
            onChange={(e) => {
              setZone(e.target.value);
              setSaved(false);
            }}
          />
        </label>
      </div>

      <datalist id="timezone-options">
        {zones.map((z) => (
          <option key={z.id} value={z.id} label={z.label} />
        ))}
      </datalist>

      {/* Offered, never applied on its own: the browser's zone is a good guess and a bad
          decision to make for someone who travels. */}
      {detected !== null && detected !== zoneValue && (
        <p className="subtext">
          This browser is in {detected}.{' '}
          <button
            type="button"
            className="link"
            onClick={() => {
              setZone(detected);
              setSaved(false);
            }}
          >
            use it
          </button>
        </p>
      )}

      <div className="detail-foot">
        <button
          type="button"
          className="primary"
          disabled={!changed || save.isPending}
          onClick={() => save.mutate()}
        >
          Save
        </button>
        {changed && (
          <button
            type="button"
            className="link"
            onClick={() => {
              setName(null);
              setZone(null);
              setError(null);
            }}
          >
            cancel
          </button>
        )}
      </div>

      {saved && (
        <p className="banner ok-banner" data-testid="profile-saved">
          Saved. Dates are now read in {me.data.timezone}.
        </p>
      )}
      {error && <p className="error">{error}</p>}
    </section>
  );
}
